import React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import {
  Box,
  Grid,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  useTheme
} from '@mui/material';
import { CompradorDrillDownData } from '../../types/drillDown';
import { useDataFormatters } from '../../Hooks/useDataFormatters';
import { formatYAxisValue } from './formatters';

interface CompradorDrillDownContentProps {
  data: CompradorDrillDownData;
}

/**
 * Componente para renderizar o conteúdo detalhado do drilldown de compradores
 * Princípio SRP: Responsável apenas pela apresentação de dados de compradores
 * Princípio OCP: Extensível sem modificar código existente
 * Princípio DIP: Depende de abstrações (hooks) não de implementações concretas
 */
export const CompradorDrillDownContent: React.FC<CompradorDrillDownContentProps> = ({ data }) => {
  const { comprador, vendas_por_mes, top_cfops, principais_produtos, transacoes_detalhadas, analise_por_ncm } = data;
  const { formatCurrency, formatNumber, formatCnpjCpf, formatMonthYear } = useDataFormatters();
  const theme = useTheme();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  if (!comprador) return null;

  // Preparar dados para gráfico de linha (evolução das vendas)
  const vendasValidas = (vendas_por_mes || []).filter(item => 
    item && typeof item.mes === 'number' && typeof item.ano === 'number'
  );
  
  const lineData = [
    {
      id: 'Valor das Vendas',
      data: vendasValidas.length > 0 
        ? vendasValidas.map(item => ({
            x: formatMonthYear(item.mes, item.ano),
            y: item.valor_vendas || 0,
            documentos: item.documentos || 0,
            transacoes: item.transacoes || 0,
            quantidade: item.quantidade || 0,
            descontos: item.descontos || 0
          }))
        : [{ x: 'Sem dados', y: 0, documentos: 0, transacoes: 0, quantidade: 0, descontos: 0 }]
    },
    {
      id: 'Débito ICMS',
      data: vendasValidas.length > 0 
        ? vendasValidas.map(item => ({
            x: formatMonthYear(item.mes, item.ano),
            y: item.debito_icms || 0,
            documentos: item.documentos || 0,
            transacoes: item.transacoes || 0
          }))
        : [{ x: 'Sem dados', y: 0, documentos: 0, transacoes: 0 }]
    }
  ];

  // Preparar dados para gráfico de barras (CFOPs)
  const cfopsValidos = (top_cfops || []).filter(item => {
    const temCfop = item && item.cfop;
    const valorNumerico = Number(item.valor_total);
    const temValorTotal = !isNaN(valorNumerico);
    const valorPositivo = valorNumerico > 0;
    
    return temCfop && temValorTotal && valorPositivo;
  });
  
  const cfopData = cfopsValidos.length > 0
    ? cfopsValidos.slice(0, 8).map(item => ({
        cfop: item.cfop,
        Valor: Number(item.valor_total),
        transacoes: Number(item.transacoes),
        produtos: Number(item.produtos_distintos),
        icms: Number(item.icms_total)
      }))
    : [];

  // Preparar dados para gráfico de produtos
  const produtosValidos = (principais_produtos || []).filter(item => {
    const temProduto = item && item.descricao_produto;
    const valorNumerico = Number(item.valor_total);
    const temValorTotal = !isNaN(valorNumerico);
    const valorPositivo = valorNumerico > 0;
    
    return temProduto && temValorTotal && valorPositivo;
  });

  const produtoData = produtosValidos.length > 0
    ? produtosValidos.slice(0, 8).map(item => ({
        produto: item.descricao_produto,
        Valor: Number(item.valor_total),
        icms: Number(item.icms_total),
        quantidade: Number(item.quantidade_total),
        transacoes: Number(item.transacoes)
      }))
    : [];

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Informações do comprador */}
        <Grid item xs={12}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'error.main' }}>
                  {comprador.nome || 'Comprador não encontrado'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Código:
                  </Typography>
                  <Chip 
                    label={comprador.codigo || 'N/A'}
                    color="default"
                    size="small"
                    variant="filled"
                    sx={{ fontWeight: 500 }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    CNPJ/CPF:
                  </Typography>
                  <Chip 
                    label={formatCnpjCpf(comprador.cnpj_cpf)}
                    color="info"
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Local:
                  </Typography>
                  <Chip 
                    label={`${comprador.municipio} - ${comprador.uf}`}
                    color="secondary"
                    size="small"
                    variant="outlined"
                  />
                  {comprador.posicao_ranking && (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                        Ranking:
                      </Typography>
                      <Chip 
                        label={`${comprador.posicao_ranking}º de ${comprador.total_compradores}`}
                        color={comprador.posicao_ranking <= 5 ? 'success' : comprador.posicao_ranking <= 10 ? 'warning' : 'default'}
                        size="small"
                        variant="filled"
                        sx={{ fontWeight: 600 }}
                        title="Posição no ranking geral de compradores"
                      />
                    </>
                  )}
                </Box>
                {comprador.endereco_completo && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {comprador.endereco_completo}
                  </Typography>
                )}
              </Box>
            </Box>
            
            <Grid container spacing={3} sx={{ mt: 2 }}>
              {/* Primeira linha - Valores Financeiros */}
              <Grid item xs={12}>
                <Typography 
                  variant="subtitle1" 
                  color="text.primary" 
                  sx={{ 
                    mb: 2, 
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 700,
                    fontSize: '1.1rem'
                  }}
                >
                  💰 Análise Financeira
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: 'error.light',
                      color: 'error.contrastText',
                      textAlign: 'center'
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        Total Vendas
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {formatCurrency(comprador.total_vendas)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: 'warning.light',
                      color: 'warning.contrastText',
                      textAlign: 'center'
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        Débito ICMS
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {formatCurrency(comprador.total_debito_icms)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: 'info.light',
                      color: 'info.contrastText',
                      textAlign: 'center'
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        Descontos
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {formatCurrency(comprador.total_descontos)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: 'info.light',
                      color: 'info.contrastText',
                      textAlign: 'center'
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        Ticket Médio
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {formatCurrency(comprador.ticket_medio)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              {/* Segunda linha - Métricas Operacionais */}
              <Grid item xs={12}>
                <Typography 
                  variant="subtitle1" 
                  color="text.primary" 
                  sx={{ 
                    mb: 2, 
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 700,
                    fontSize: '1.1rem'
                  }}
                >
                    📦 Volume e Variedade
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: 'secondary.light',
                      color: 'secondary.contrastText',
                      textAlign: 'center'
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        Quantidade
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {formatNumber(comprador.quantidade_total)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: 'warning.light',
                      color: 'warning.contrastText',
                      textAlign: 'center'
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        Produtos
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {formatNumber(comprador.produtos_distintos)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: 'success.light',
                      color: 'success.contrastText',
                      textAlign: 'center'
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        Documentos
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {formatNumber(comprador.total_documentos)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: 'primary.light',
                      color: 'primary.contrastText',
                      textAlign: 'center'
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        Transações
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {formatNumber(comprador.total_transacoes)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              {/* Terceira linha - Período de Atividade */}
              <Grid item xs={12}>
                <Typography 
                  variant="subtitle1" 
                  color="text.primary" 
                  sx={{ 
                    mb: 2, 
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 700,
                    fontSize: '1.1rem'
                  }}
                >
                  🤝 Histórico de Relacionamento
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: 'success.light',
                      color: 'success.contrastText',
                      textAlign: 'center'
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        Primeira Venda
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {comprador.primeira_venda ? new Date(comprador.primeira_venda).toLocaleDateString('pt-BR') : 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: 'error.light',
                      color: 'error.contrastText',
                      textAlign: 'center'
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        Última Venda
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {comprador.ultima_venda ? new Date(comprador.ultima_venda).toLocaleDateString('pt-BR') : 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Gráfico de evolução temporal */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              📈 Evolução das Vendas e Débitos ICMS
            </Typography>
            <Box sx={{ height: 400 }}>
              <ResponsiveLine
                data={lineData}
                margin={{ top: 20, right: 30, bottom: 60, left: 60 }}
                xScale={{ type: 'point' }}
                yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false }}
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: -45,
                  legend: 'Período',
                  legendOffset: 50,
                  legendPosition: 'middle'
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: 'Valor (R$)',
                  legendOffset: -50,
                  legendPosition: 'middle',
                  format: formatYAxisValue
                }}
                pointSize={8}
                pointColor={{ theme: 'background' }}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                pointLabelYOffset={-12}
                useMesh={true}
                legends={[
                  {
                    anchor: 'top',
                    direction: 'row',
                    justify: false,
                    translateX: 0,
                    translateY: -20,
                    itemsSpacing: 40, // Espaçamento equilibrado
                    itemDirection: 'left-to-right',
                    itemWidth: 100, // Largura compacta (símbolo + texto próximos)
                    itemHeight: 20,
                    itemOpacity: 0.75,
                    symbolSize: 12,
                    symbolShape: 'circle',
                    symbolBorderColor: 'rgba(0, 0, 0, .5)',
                    effects: [
                      {
                        on: 'hover',
                        style: {
                          itemBackground: 'rgba(0, 0, 0, .03)',
                          itemOpacity: 1
                        }
                      }
                    ]
                  }
                ]}
                tooltip={({ point }) => {
                  const data = point.data as any;
                  return (
                    <Box
                      sx={{
                        background: theme.palette.background.paper,
                        borderRadius: 2,
                        boxShadow: 3,
                        p: 2,
                        minWidth: 200,
                        border: `1px solid ${theme.palette.divider}`
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        📊 {point.serieId}
                      </Typography>
                      <Typography variant="h6" color="success.main" sx={{ mb: 1 }}>
                        {formatCurrency(point.data.y as number)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Período: {String(point.data.x)}
                      </Typography>
                      {data.documentos && (
                        <Typography variant="body2" color="text.secondary">
                          Documentos: {data.documentos}
                        </Typography>
                      )}
                      {data.transacoes && (
                        <Typography variant="body2" color="text.secondary">
                          Transações: {data.transacoes}
                        </Typography>
                      )}
                    </Box>
                  );
                }}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Gráfico de CFOPs */}
        {cfopData.length > 0 && (
          <Grid item xs={12} md={12}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                🏷️ Top CFOPs Utilizados
              </Typography>
              <Box sx={{ height: 400 }}>
                <ResponsiveBar
                  data={cfopData}
                  keys={['Valor']}
                  indexBy="cfop"
                  margin={{ top: 20, right: 30, bottom: 60, left: 60 }}
                  padding={0.3}
                  valueScale={{ type: 'linear' }}
                  indexScale={{ type: 'band', round: true }}
                  colors={{ scheme: 'nivo' }}
                  borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                  axisTop={null}
                  axisRight={null}
                  axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: -45,
                    legend: 'CFOP',
                    legendPosition: 'middle',
                    legendOffset: 50
                  }}
                  axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: 'Valor (R$)',
                    legendPosition: 'middle',
                    legendOffset: -50,
                    format: formatYAxisValue
                  }}
                  labelSkipWidth={12}
                  labelSkipHeight={12}
                  labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                  tooltip={({ data }) => (
                    <Box
                      sx={{
                        background: theme.palette.background.paper,
                        borderRadius: 2,
                        boxShadow: 3,
                        p: 2,
                        minWidth: 200,
                        border: `1px solid ${theme.palette.divider}`
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        🏷️ CFOP {data.cfop}
                      </Typography>
                      <Typography variant="h6" color="success.main" sx={{ mb: 1 }}>
                        {formatCurrency(data.Valor)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ICMS: {formatCurrency(data.icms)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Transações: {data.transacoes}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Produtos: {data.produtos}
                      </Typography>
                    </Box>
                  )}
                />
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Gráfico de Produtos */}
        {produtoData.length > 0 && (
          <Grid item xs={12} md={12}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                📦 Principais Produtos Vendidos
              </Typography>
              <Box sx={{ height: 400 }}>
                <ResponsiveBar
                  data={produtoData}
                  keys={['Valor']}
                  indexBy="produto"
                  margin={{ top: 20, right: 30, bottom: 60, left: 60 }}
                  padding={0.3}
                  valueScale={{ type: 'linear' }}
                  indexScale={{ type: 'band', round: true }}
                  colors={{ scheme: 'category10' }}
                  borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                  axisTop={null}
                  axisRight={null}
                  axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: -45,
                    legend: 'Produto',
                    legendPosition: 'middle',
                    legendOffset: 50
                  }}
                  axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: 'Valor (R$)',
                    legendPosition: 'middle',
                    legendOffset: -50,
                    format: formatYAxisValue
                  }}
                  labelSkipWidth={12}
                  labelSkipHeight={12}
                  labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                  tooltip={({ data }) => (
                    <Box
                      sx={{
                        background: theme.palette.background.paper,
                        borderRadius: 2,
                        boxShadow: 3,
                        p: 2,
                        minWidth: 200,
                        border: `1px solid ${theme.palette.divider}`
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        📦 {data.produto}
                      </Typography>
                      <Typography variant="h6" color="success.main" sx={{ mb: 1 }}>
                        {formatCurrency(data.Valor)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ICMS: {formatCurrency(data.icms)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Quantidade: {formatNumber(data.quantidade)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Transações: {data.transacoes}
                      </Typography>
                    </Box>
                  )}
                />
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Tabela de transações detalhadas */}
        {transacoes_detalhadas && transacoes_detalhadas.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                📋 Transações Detalhadas
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Documento</TableCell>
                      <TableCell>CFOP</TableCell>
                      <TableCell>Produto</TableCell>
                      <TableCell align="right">Valor Item</TableCell>
                      <TableCell align="right">ICMS</TableCell>
                      <TableCell align="right">Quantidade</TableCell>
                      <TableCell align="right">Desconto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transacoes_detalhadas
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((transacao, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            {transacao.dt_emissao ? new Date(transacao.dt_emissao).toLocaleDateString('pt-BR') : 'N/A'}
                          </TableCell>
                          <TableCell>{transacao.num_doc}</TableCell>
                          <TableCell>{transacao.cfop}</TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {transacao.cod_item}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {transacao.descr_compl}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">{formatCurrency(transacao.vl_item)}</TableCell>
                          <TableCell align="right">{formatCurrency(transacao.vl_icms)}</TableCell>
                          <TableCell align="right">{formatNumber(transacao.qtd)}</TableCell>
                          <TableCell align="right">{formatCurrency(transacao.desconto)}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={transacoes_detalhadas.length}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                labelRowsPerPage="Linhas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </Paper>
          </Grid>
        )}

        {/* Análise por NCM */}
        {analise_por_ncm && analise_por_ncm.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                🏷️ Análise por NCM
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>NCM</TableCell>
                      <TableCell>Descrição</TableCell>
                      <TableCell align="right">Valor Total</TableCell>
                      <TableCell align="right">ICMS Total</TableCell>
                      <TableCell align="right">Quantidade</TableCell>
                      <TableCell align="right">Produtos Diferentes</TableCell>
                      <TableCell align="right">Transações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analise_por_ncm.map((ncm, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Chip 
                            label={ncm.ncm} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{ncm.descricao_ncm}</TableCell>
                        <TableCell align="right">{formatCurrency(ncm.valor_total)}</TableCell>
                        <TableCell align="right">{formatCurrency(ncm.icms_total)}</TableCell>
                        <TableCell align="right">{formatNumber(ncm.quantidade_total)}</TableCell>
                        <TableCell align="right">{formatNumber(ncm.produtos_diferentes)}</TableCell>
                        <TableCell align="right">{formatNumber(ncm.transacoes)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}; 